import I18n from 'I18n';
import getLang from 'I18n/utils/getLang';
import { dateFieldForCategory, EnrollmentStatusesForBetaStatus, ScheduledUpdatesViews } from './constants';
import { dateStringToUnixString } from '../app/utils/dateUtils';
import { TrackingSubscreen } from '../app/utils/tracking';
// These filters should not be included for no PUs checking
export const EXCLUDED_FILTERS = ['limit', 'offset', 'recommended', 'rolloutState'];
export const EXCLUDED_FILTERS_RECOMMENDED = ['limit', 'offset', 'recommended'];
export const EXCLUDED_FILTERS_SCHEDULED_FOR_RELEASE = ['limit', 'offset', 'recommended', 'startReleaseDate', 'endReleaseDate', 'rolloutState', 'sortReleaseDate'];
export const EXCLUDED_FILTERS_YOUR_BETAS = ['limit', 'offset', 'enrollmentStatus', 'rolloutState'];
export const EXCLUDED_FILTERS_NEW_TO_YOU = ['limit', 'offset', 'startStageDate'];
const dateRangeForCategory = ({
  calendarTimestamp,
  category,
  endDate,
  startDate,
  view
}) => {
  if (category === 'scheduled-for-release') {
    if (view === ScheduledUpdatesViews.CALENDAR && calendarTimestamp) {
      const timestampNow = I18n.moment.utc().valueOf();
      const startTimestamp = Math.max(calendarTimestamp, timestampNow);
      const startOfWeek = I18n.moment.utc(startTimestamp).startOf('week');
      const endOfWeek = I18n.moment.utc(calendarTimestamp).endOf('week');
      return {
        endReleaseDate: dateStringToUnixString(endOfWeek.format()),
        endStageDate: null,
        startReleaseDate: dateStringToUnixString(startOfWeek.format()),
        startStageDate: null
      };
    }
    return {
      endReleaseDate: null,
      endStageDate: null,
      startReleaseDate: dateStringToUnixString(I18n.moment.utc().startOf('day').format()),
      startStageDate: null
    };
  }
  const endDateUnix = endDate ? dateStringToUnixString(endDate) : null;
  const startDateUnix = startDate ? dateStringToUnixString(startDate) : null;
  const dateField = category ? dateFieldForCategory[category] : 'stageDate';
  if (dateField === 'stageDate') {
    return {
      endReleaseDate: null,
      endStageDate: endDateUnix,
      startReleaseDate: null,
      startStageDate: startDateUnix
    };
  } else {
    return {
      endReleaseDate: endDateUnix,
      endStageDate: null,
      startReleaseDate: startDateUnix,
      startStageDate: null
    };
  }
};
const getRolloutStateName = (rolloutState, view) => {
  switch (rolloutState) {
    case 'live':
      return 'LIVE';
    case 'your-betas':
    case 'in-beta':
      return ['PRIVATE_BETA', 'PUBLIC_BETA'];
    case 'in-development':
      return 'IN_DEVELOPMENT';
    case 'sunset':
      return 'SUNSET';
    case 'scheduled-for-release':
      return ['IN_DEVELOPMENT', 'PHASED_ROLLOUT', 'PRIVATE_BETA', 'PUBLIC_BETA', 'UPCOMING_SUNSET', ...(view === 'CALENDAR' ? ['LIVE'] : [])];
    case 'all':
    default:
      return null;
  }
};
const rolloutStateForCategory = (category, rolloutStateFromParams, view) => {
  if (category === 'recommended' && rolloutStateFromParams) {
    const rolloutStateFilters = rolloutStateFromParams.split(',').reduce( /* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */
    (acc, params) => acc.concat(getRolloutStateName(params, view) || []), []);
    return {
      rolloutState: rolloutStateFilters
    };
  } else {
    return {
      rolloutState: getRolloutStateName(category, view)
    };
  }
};
const sortsForCategory = category => {
  const sortStageDate = null;
  let sortReleaseDate = null;
  if (category === 'scheduled-for-release') {
    sortReleaseDate = 'ASC';
  }
  return {
    sortReleaseDate,
    sortStageDate
  };
};
const getEnrollmentStatusesFromFilter = betaStatus => Array.isArray(betaStatus) ? betaStatus.reduce((enrollmentStatuses, status) => {
  enrollmentStatuses.push(...EnrollmentStatusesForBetaStatus[status]);
  return enrollmentStatuses;
}, []) : betaStatus && EnrollmentStatusesForBetaStatus[betaStatus];
export const getRequestParamsForGetRolloutProductUpdates = params => {
  return Object.assign({
    enrollmentStatus: getEnrollmentStatusesFromFilter(params.betaStatus),
    featureGroup: params.app,
    impactLevels: params.impactLevels,
    isFavorite: params.isFavorite,
    hasAiFeature: params.hasAiFeature,
    limit: params.view === 'CALENDAR' ? 50 : params.limit,
    offset: params.offset,
    product: params.product,
    query: params.puQuery,
    recommended: params.recommended,
    rolloutType: params.type
  }, dateRangeForCategory(params), rolloutStateForCategory(params.category, params.rolloutState, params.view), sortsForCategory(params.category));
};
export const getRequestParamsForGetRolloutProductUpdatesByCategory = (category, view) => getRequestParamsForGetRolloutProductUpdates({
  app: null,
  betaStatus: null,
  calendarTimestamp: null,
  category,
  endDate: null,
  impactLevels: null,
  isFavorite: null,
  hasAiFeature: null,
  limit: null,
  offset: null,
  product: null,
  puQuery: null,
  recommended: null,
  rolloutState: null,
  startDate: null,
  type: null,
  view
});
export const impactLevelToTagText = impactLevel => {
  if (impactLevel === null) {
    return 'NO DATA';
  }
  switch (true) {
    case impactLevel >= 7:
      return 'HIGH';
    case impactLevel >= 3 && impactLevel <= 6:
      return 'MEDIUM';
    case impactLevel >= 0 && impactLevel <= 2:
      return 'LOW';
    default:
      return 'NO DATA';
  }
};
export const getTrackingSubscreenTab = (category, view) => {
  switch (category) {
    case 'all':
      return TrackingSubscreen.ALL;
    case 'in-beta':
      return TrackingSubscreen.IN_BETA;
    case 'in-development':
      return TrackingSubscreen.IN_DEVELOPMENT;
    case 'live':
      return TrackingSubscreen.LIVE;
    case 'new-to-you':
      return TrackingSubscreen.NEW_TO_YOU;
    case 'recommended':
      return TrackingSubscreen.RECOMMENDED;
    case 'sunset':
      return TrackingSubscreen.RECOMMENDED;
    case 'scheduled-for-release':
      return view === 'TABLE' ? TrackingSubscreen.SCHEDULED_FOR_RELEASE_TABLE : TrackingSubscreen.SCHEDULED_FOR_RELEASE_CALENDAR;
    case 'your-betas':
      return TrackingSubscreen.YOUR_BETAS;
    default:
      return TrackingSubscreen.ALL;
  }
};
export const excludeNullable = v => Boolean(v);
export const productToTranslationKey = product => {
  if (product.startsWith('core-seats')) {
    const coreSeatsRegex = /core-seats/;
    return product.replace(coreSeatsRegex, 'smart-crm');
  }
  return product;
};

/**
 *  @temporary Remove this Spotlight banner
 *  @expiration 2025-09-17
 *  @deprecated
 */
export const getLocalizedSpotlightLink = () => {
  const url = 'https://www.hubspot.com/spotlight?hubs_content-cta=in-app';
  const swapBaseMap = {
    ja: 'www.hubspot.jp',
    de: 'www.hubspot.de',
    es: 'www.hubspot.es',
    pt: 'br.hubspot.com',
    fr: 'www.hubspot.fr'
  };
  const swapBase = swapBaseMap[getLang()];
  if (!swapBase) {
    return url;
  }
  return url.replace('www.hubspot.com', swapBase);
};